import {
  fetchAwsApi,
  getAuthenticatedAccountId,
  getAuthToken,
  isAuthenticatedSession,
  parseApiResponse,
} from '../security';
import {
  fromAccountLanguageCode,
  toAccountLanguageCode,
  type AppLocale,
} from '../i18n/locale';

type UnknownRecord = Record<string, unknown>;

export type AccountInfoRecord = UnknownRecord;
export type AccountCookiePreference = 'functional' | 'performance';

let accountInfoRequestPromise: Promise<AccountInfoRecord | null> | null = null;
let accountInfoRequestKey = '';
let accountInfoCache: AccountInfoRecord | null = null;
let accountInfoCacheKey = '';
let hasAccountInfoCache = false;

function getAccountInfoCacheKey() {
  return `${getAuthenticatedAccountId() || 'authenticated'}:${getAuthToken()}`;
}

export function clearAccountInfoCache() {
  accountInfoCache = null;
  accountInfoCacheKey = '';
  hasAccountInfoCache = false;
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseJsonString(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function hasAccountIdentity(value: UnknownRecord) {
  return Boolean(
    value.account_id ??
    value.accountId ??
    value.id ??
    value.email ??
    value.username ??
    value.first_name ??
    value.firstName ??
    value.last_name ??
    value.lastName
  );
}

function hasProfileMedia(value: UnknownRecord) {
  return Boolean(
    value.profileImg ??
    value.profile_img ??
    value.profileImage ??
    value.profile_picture ??
    value.profilePicture ??
    value.profileImgS3Key ??
    value.profile_img_s3_key ??
    value.profileAvatar ??
    value.profile_avatar ??
    value.profileAvatarS3Key ??
    value.profile_avatar_s3_key ??
    value.profileVideo
  );
}

function hasAccountCredits(value: UnknownRecord) {
  return Number.isFinite(
    Number(
      value.credits ??
      value.credit ??
      value.available_credits ??
      value.availableCredits
    )
  );
}

function hasSavedMeasurements(value: UnknownRecord) {
  const nestedCandidates = [
    value.measurements,
    value.measurement_profile,
    value.measurementProfile,
    value.fit_profile,
    value.fitProfile,
    value.data_3dlook,
    value.data3dlook,
    value.data_3dlook_json,
    value.data3dlookJson,
    value,
  ];

  return nestedCandidates.some((candidate) => {
    if (!isRecord(candidate)) {
      return false;
    }

    const measurementSource =
      isRecord(candidate.measurements) ? candidate.measurements : candidate;

    return Object.values(measurementSource).some((entry) => {
      const numericValue =
        typeof entry === 'number'
          ? entry
          : typeof entry === 'string'
            ? Number.parseFloat(entry)
            : Number.NaN;

      return Number.isFinite(numericValue) && numericValue > 0;
    });
  });
}

function extractAccountLocaleFromRecord(value: UnknownRecord): AppLocale | null {
  return fromAccountLanguageCode(
    value.lang ??
    value.language
  );
}

function hasLanguagePreference(value: UnknownRecord) {
  return extractAccountLocaleFromRecord(value) !== null;
}

function hasCookiePreference(value: UnknownRecord) {
  return value.cookies === null || Array.isArray(value.cookies);
}

function isAccountInfoCandidate(value: UnknownRecord) {
  return (
    hasAccountIdentity(value)
    || hasProfileMedia(value)
    || hasAccountCredits(value)
    || hasSavedMeasurements(value)
    || hasLanguagePreference(value)
    || hasCookiePreference(value)
  );
}

export function extractAccountInfoFromPayload(payload: unknown): AccountInfoRecord | null {
  const queue: unknown[] = [payload];
  const visited = new WeakSet<object>();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== 'object') {
      continue;
    }

    if (visited.has(current)) {
      continue;
    }
    visited.add(current);

    if (Array.isArray(current)) {
      queue.push(...current);
      continue;
    }
    if (!isRecord(current)) {
      continue;
    }

    if (isRecord(current.account) && isAccountInfoCandidate(current.account)) {
      return current.account;
    }
    if (isRecord(current.user) && isAccountInfoCandidate(current.user)) {
      return current.user;
    }
    if (isAccountInfoCandidate(current)) {
      return current;
    }

    queue.push(
      current.profile,
      current.account,
      current.user,
      current.data,
      current.result,
      current.body,
      current.item,
      current.items,
      current.records,
      current.rows,
      ...Object.values(current)
    );
  }

  return null;
}

export function hasUnreadNotifications(accountInfo: AccountInfoRecord | null) {
  const unreadNotifications = accountInfo?.unread_notifications;

  if (Array.isArray(unreadNotifications)) {
    return unreadNotifications.length > 0;
  }

  if (typeof unreadNotifications === 'string') {
    const normalizedValue = unreadNotifications.trim();
    if (!normalizedValue) {
      return false;
    }

    const parsedValue = parseJsonString(normalizedValue);
    if (Array.isArray(parsedValue)) {
      return parsedValue.length > 0;
    }

    if (isRecord(parsedValue)) {
      return Object.keys(parsedValue).length > 0;
    }

    return normalizedValue !== '{}' && normalizedValue !== '[]';
  }

  if (isRecord(unreadNotifications)) {
    return Object.keys(unreadNotifications).length > 0;
  }

  return false;
}

export function extractAccountLocale(payload: unknown): AppLocale | null {
  const queue: unknown[] = [payload];
  const visited = new WeakSet<object>();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== 'object') {
      continue;
    }

    if (visited.has(current)) {
      continue;
    }
    visited.add(current);

    if (Array.isArray(current)) {
      queue.push(...current);
      continue;
    }
    if (!isRecord(current)) {
      continue;
    }

    const directLocale = extractAccountLocaleFromRecord(current);
    if (directLocale) {
      return directLocale;
    }

    if (isRecord(current.account)) {
      const nestedAccountLocale = extractAccountLocaleFromRecord(current.account);
      if (nestedAccountLocale) {
        return nestedAccountLocale;
      }
    }

    if (isRecord(current.user)) {
      const nestedUserLocale = extractAccountLocaleFromRecord(current.user);
      if (nestedUserLocale) {
        return nestedUserLocale;
      }
    }

    queue.push(
      current.profile,
      current.account,
      current.user,
      current.data,
      current.result,
      current.body,
      current.item,
      current.items,
      current.records,
      current.rows,
      ...Object.values(current)
    );
  }

  return null;
}

export function extractAccountCookies(payload: unknown): AccountCookiePreference[] | null | undefined {
  const queue: unknown[] = [payload];
  const visited = new WeakSet<object>();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== 'object') {
      continue;
    }

    if (visited.has(current)) {
      continue;
    }
    visited.add(current);

    if (Array.isArray(current)) {
      queue.push(...current);
      continue;
    }
    if (!isRecord(current)) {
      continue;
    }

    const directCookies = current.cookies;
    if (directCookies === null) {
      return null;
    }
    if (Array.isArray(directCookies)) {
      return directCookies.filter(
        (entry): entry is AccountCookiePreference => entry === 'functional' || entry === 'performance'
      );
    }

    queue.push(
      current.profile,
      current.account,
      current.user,
      current.data,
      current.result,
      current.body,
      current.item,
      current.items,
      current.records,
      current.rows,
      ...Object.values(current)
    );
  }

  return undefined;
}

export async function fetchAccountInfo() {
  if (!isAuthenticatedSession()) {
    return null;
  }

  const requestKey = getAccountInfoCacheKey();
  if (hasAccountInfoCache && accountInfoCacheKey === requestKey) {
    return accountInfoCache;
  }

  if (accountInfoRequestPromise && accountInfoRequestKey === requestKey) {
    return accountInfoRequestPromise;
  }

  accountInfoRequestKey = requestKey;
  accountInfoRequestPromise = (async () => {
    const response = await fetchAwsApi('/api/update_account', { method: 'GET' });
    const payload = await parseApiResponse(response);

    if (!response.ok) {
      return null;
    }

    const nextAccountInfo = extractAccountInfoFromPayload(payload);
    accountInfoCache = nextAccountInfo;
    accountInfoCacheKey = requestKey;
    hasAccountInfoCache = true;
    return nextAccountInfo;
  })()
    .catch(() => null)
    .finally(() => {
      if (accountInfoRequestKey === requestKey) {
        accountInfoRequestPromise = null;
        accountInfoRequestKey = '';
      }
    });

  return accountInfoRequestPromise;
}

export async function updateAccountLanguagePreference(locale: AppLocale) {
  if (!isAuthenticatedSession()) {
    return { lang: toAccountLanguageCode(locale) };
  }

  const response = await fetchAwsApi('/api/update_account', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      lang: toAccountLanguageCode(locale),
    }),
  });
  const payload = await parseApiResponse(response);

  if (!response.ok) {
    throw new Error('Unable to update account language preference.');
  }

  clearAccountInfoCache();
  return payload;
}

export async function updateAccountCookiePreferences(cookies: AccountCookiePreference[]) {
  if (!isAuthenticatedSession()) {
    return { cookies };
  }

  const response = await fetchAwsApi('/api/update_account', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      cookies,
    }),
  });
  const payload = await parseApiResponse(response);

  if (!response.ok) {
    throw new Error('Unable to update cookie preferences.');
  }

  clearAccountInfoCache();
  return payload;
}
